/** SSR/workers-safe header chrome color helpers (no DOM APIs). */

import {
  normalizeNavBarBackgroundColor,
  navBarBackgroundColorForPicker,
} from './nav-bar-background-shared';

export {
  normalizeNavBarBackgroundColor as normalizeHeaderChromeColor,
  navBarBackgroundColorForPicker as headerChromeColorForPicker,
};

export const HEADER_CHROME_BACKGROUND_PICKER_DEFAULT = '#ffffff';

export const STOREFRONT_HEADER_CHROME_BG_VAR = '--storefront-header-chrome-bg';
export const STOREFRONT_HEADER_CHROME_HOVER_BG_VAR = '--storefront-header-chrome-hover-bg';
export const STOREFRONT_SEARCH_PANEL_BG_VAR = '--storefront-search-panel-bg';
export const STOREFRONT_SEARCH_PANEL_HOVER_BG_VAR = '--storefront-search-panel-hover-bg';
export const STOREFRONT_ACCOUNT_PANEL_BG_VAR = '--storefront-account-panel-bg';
export const STOREFRONT_ACCOUNT_PANEL_HOVER_BG_VAR = '--storefront-account-panel-hover-bg';
export const STOREFRONT_CART_DRAWER_BG_VAR = '--storefront-cart-drawer-bg';
export const STOREFRONT_CART_DRAWER_HOVER_BG_VAR = '--storefront-cart-drawer-hover-bg';

const CHROME_SELECTOR = '[data-storefront-header-chrome-row="true"]';

export interface StorefrontHeaderChromeColorSettings {
  chromeBackgroundColor?: unknown;
  chromeHoverBackgroundColor?: unknown;
  searchPanelBackgroundColor?: unknown;
  searchPanelHoverBackgroundColor?: unknown;
  accountPanelBackgroundColor?: unknown;
  accountPanelHoverBackgroundColor?: unknown;
  cartDrawerBackgroundColor?: unknown;
  cartDrawerHoverBackgroundColor?: unknown;
}

const CSS_VARS: Record<keyof StorefrontHeaderChromeColorSettings, string> = {
  chromeBackgroundColor: STOREFRONT_HEADER_CHROME_BG_VAR,
  chromeHoverBackgroundColor: STOREFRONT_HEADER_CHROME_HOVER_BG_VAR,
  searchPanelBackgroundColor: STOREFRONT_SEARCH_PANEL_BG_VAR,
  searchPanelHoverBackgroundColor: STOREFRONT_SEARCH_PANEL_HOVER_BG_VAR,
  accountPanelBackgroundColor: STOREFRONT_ACCOUNT_PANEL_BG_VAR,
  accountPanelHoverBackgroundColor: STOREFRONT_ACCOUNT_PANEL_HOVER_BG_VAR,
  cartDrawerBackgroundColor: STOREFRONT_CART_DRAWER_BG_VAR,
  cartDrawerHoverBackgroundColor: STOREFRONT_CART_DRAWER_HOVER_BG_VAR,
};

function coerceChromeColorSettings(
  input?: StorefrontHeaderChromeColorSettings | unknown,
): StorefrontHeaderChromeColorSettings {
  if (input !== null && typeof input === 'object' && !Array.isArray(input)) {
    return input as StorefrontHeaderChromeColorSettings;
  }
  return {};
}

export function resolveHeaderChromeStyle(
  settings: StorefrontHeaderChromeColorSettings = {},
): Record<string, string> | undefined {
  const style: Record<string, string> = {};
  for (const key of Object.keys(CSS_VARS) as Array<keyof StorefrontHeaderChromeColorSettings>) {
    const color = normalizeNavBarBackgroundColor(settings[key]);
    if (color) {
      style[CSS_VARS[key]] = color;
    }
  }
  const chromeBg = normalizeNavBarBackgroundColor(settings.chromeBackgroundColor);
  if (chromeBg) {
    style.backgroundColor = chromeBg;
  }
  return Object.keys(style).length > 0 ? style : undefined;
}

export function resolveHeaderChromeStyleAttribute(
  settings: StorefrontHeaderChromeColorSettings = {},
): string {
  const resolved = coerceChromeColorSettings(settings);
  const styleParts: string[] = [];
  const dataParts: string[] = [];

  const chromeBg = normalizeNavBarBackgroundColor(resolved.chromeBackgroundColor);
  if (chromeBg) {
    styleParts.push(
      `${STOREFRONT_HEADER_CHROME_BG_VAR}:${chromeBg}`,
      `background-color:${chromeBg} !important`,
    );
    dataParts.push(` data-chrome-background="${chromeBg}"`);
  }

  const entries: Array<[keyof StorefrontHeaderChromeColorSettings, string, string]> = [
    ['chromeHoverBackgroundColor', STOREFRONT_HEADER_CHROME_HOVER_BG_VAR, 'chrome-hover-background'],
    ['searchPanelBackgroundColor', STOREFRONT_SEARCH_PANEL_BG_VAR, 'search-panel-background'],
    ['searchPanelHoverBackgroundColor', STOREFRONT_SEARCH_PANEL_HOVER_BG_VAR, 'search-panel-hover-background'],
    ['accountPanelBackgroundColor', STOREFRONT_ACCOUNT_PANEL_BG_VAR, 'account-panel-background'],
    ['accountPanelHoverBackgroundColor', STOREFRONT_ACCOUNT_PANEL_HOVER_BG_VAR, 'account-panel-hover-background'],
    ['cartDrawerBackgroundColor', STOREFRONT_CART_DRAWER_BG_VAR, 'cart-drawer-background'],
    ['cartDrawerHoverBackgroundColor', STOREFRONT_CART_DRAWER_HOVER_BG_VAR, 'cart-drawer-hover-background'],
  ];

  for (const [key, cssVar, dataAttr] of entries) {
    const color = normalizeNavBarBackgroundColor(resolved[key]);
    if (color) {
      styleParts.push(`${cssVar}:${color}`);
      dataParts.push(` data-${dataAttr}="${color}"`);
    }
  }

  if (styleParts.length === 0) return '';
  return ` style="${styleParts.join(';')}"${dataParts.join('')}`;
}

export function buildHeaderChromeThemeCss(settings: StorefrontHeaderChromeColorSettings): string {
  const chromeBg = normalizeNavBarBackgroundColor(settings.chromeBackgroundColor);
  const chromeHover = normalizeNavBarBackgroundColor(settings.chromeHoverBackgroundColor);
  const searchPanelBg = normalizeNavBarBackgroundColor(settings.searchPanelBackgroundColor);
  const searchPanelHover = normalizeNavBarBackgroundColor(settings.searchPanelHoverBackgroundColor);
  const accountPanelBg = normalizeNavBarBackgroundColor(settings.accountPanelBackgroundColor);
  const accountPanelHover = normalizeNavBarBackgroundColor(settings.accountPanelHoverBackgroundColor);
  const cartDrawerBg = normalizeNavBarBackgroundColor(settings.cartDrawerBackgroundColor);
  const cartDrawerHover = normalizeNavBarBackgroundColor(settings.cartDrawerHoverBackgroundColor);

  const rootDeclarations: string[] = [];
  if (chromeBg) {
    rootDeclarations.push(
      `${STOREFRONT_HEADER_CHROME_BG_VAR}:${chromeBg}`,
      `background-color:${chromeBg}!important`,
    );
  }
  if (chromeHover) rootDeclarations.push(`${STOREFRONT_HEADER_CHROME_HOVER_BG_VAR}:${chromeHover}`);
  if (searchPanelBg) rootDeclarations.push(`${STOREFRONT_SEARCH_PANEL_BG_VAR}:${searchPanelBg}`);
  if (searchPanelHover) {
    rootDeclarations.push(`${STOREFRONT_SEARCH_PANEL_HOVER_BG_VAR}:${searchPanelHover}`);
  }
  if (accountPanelBg) rootDeclarations.push(`${STOREFRONT_ACCOUNT_PANEL_BG_VAR}:${accountPanelBg}`);
  if (accountPanelHover) {
    rootDeclarations.push(`${STOREFRONT_ACCOUNT_PANEL_HOVER_BG_VAR}:${accountPanelHover}`);
  }
  if (cartDrawerBg) rootDeclarations.push(`${STOREFRONT_CART_DRAWER_BG_VAR}:${cartDrawerBg}`);
  if (cartDrawerHover) {
    rootDeclarations.push(`${STOREFRONT_CART_DRAWER_HOVER_BG_VAR}:${cartDrawerHover}`);
  }

  const rules: string[] = [];
  if (rootDeclarations.length > 0) {
    rules.push(`${CHROME_SELECTOR}{${rootDeclarations.join(';')}}`);
  }

  if (chromeHover) {
    rules.push(
      `${CHROME_SELECTOR} .user-menu-trigger:hover,${CHROME_SELECTOR} .cart-button:hover,${CHROME_SELECTOR} .universal-search__form .input-group:focus-within{background-color:${chromeHover}!important}`,
    );
  }

  if (searchPanelHover) {
    rules.push(
      `${CHROME_SELECTOR} .universal-search__item:hover{background-color:${searchPanelHover}!important}`,
    );
  }

  if (accountPanelHover) {
    rules.push(
      `${CHROME_SELECTOR} .user-menu-item:hover,${CHROME_SELECTOR} .user-menu-item a:hover{background-color:${accountPanelHover}!important}`,
    );
  }

  if (cartDrawerBg) {
    rules.push(`.cart-drawer-panel{background-color:${cartDrawerBg}!important}`);
  }

  if (cartDrawerHover) {
    rules.push(
      `.cart-drawer-panel .cart-item:hover{background-color:${cartDrawerHover}!important}`,
    );
  }

  return rules.join('');
}
